/**
 * Draft Detection Hook
 * 
 * Detects and lists all available drafts in localStorage
 * Filters by current user and organization for isolation
 */

import { useState, useEffect } from 'react';
import { getStoragePrefix, parseStorageKey, getCurrentUserAndOrg } from '../utils/storageUtils';
import { JobPostMode } from '../types';

export interface DraftInfo {
  exists: boolean;
  mode: JobPostMode;
  /** Unique draft identifier (UUID for new/clone, jobPostId for edit). Used to re-key the persistence hook on restoration. */
  draftId: string;
  /** @deprecated Use draftId. Kept for backward compat with edit-mode display. */
  jobPostId?: string;
  lastSavedAt?: number;
  step?: number;
  preview?: {
    title?: string;
    position?: string;
  };
  storageKey: string;
  userId?: string;
  orgId?: string;
}

export const useDraftDetection = (): DraftInfo[] => {
  const [drafts, setDrafts] = useState<DraftInfo[]>([]);

  useEffect(() => {
    const detectDrafts = () => {
      const detectedDrafts: DraftInfo[] = [];
      const { userId: currentUserId, orgId: currentOrgId } = getCurrentUserAndOrg();
      const STORAGE_PREFIX = getStoragePrefix();
      
      // Only show drafts if user is logged in
      if (!currentUserId || !currentOrgId) {
        setDrafts([]);
        return;
      }
      
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(STORAGE_PREFIX)) {
            // Parse storage key to extract user/org info
            const keyInfo = parseStorageKey(key);
            if (!keyInfo) continue;
            
            // Filter by current user and org
            if (keyInfo.userId !== currentUserId || keyInfo.orgId !== currentOrgId) {
              continue;
            }
            
            const item = localStorage.getItem(key);
            if (item) {
              try {
                const parsed = JSON.parse(item);
                if (parsed && parsed.timestamp) {
                  detectedDrafts.push({
                    exists: true,
                    mode: keyInfo.mode,
                    draftId: keyInfo.draftId,
                    // For edit mode the draftId IS the jobPostId; expose it for display
                    jobPostId: keyInfo.mode === 'edit' ? keyInfo.draftId : undefined,
                    lastSavedAt: parsed.timestamp,
                    step: parsed.currentStep || 1,
                    preview: {
                      title: parsed.step1?.title,
                      position: parsed.step1?.position,
                    },
                    storageKey: key,
                    userId: keyInfo.userId,
                    orgId: keyInfo.orgId,
                  });
                }
              } catch (e) {
                // Invalid draft, skip
                console.warn('Invalid draft format:', key);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error detecting drafts:', error);
      }
      
      // Sort by last saved (most recent first)
      detectedDrafts.sort((a, b) => 
        (b.lastSavedAt || 0) - (a.lastSavedAt || 0)
      );
      
      setDrafts(detectedDrafts);
    };
    
    detectDrafts();
    
    // Listen for storage changes to update draft list
    const handleStorageChange = () => {
      detectDrafts();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return drafts;
};

